import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { MicOff } from "lucide-react";
import {
  AudioConfig,
  AudioInputStream,
  AudioStreamFormat,
  ConversationTranscriber,
  type ConversationTranscriptionEventArgs,
  PropertyId,
  type PushAudioInputStream,
  ResultReason,
  SpeechConfig,
} from "microsoft-cognitiveservices-speech-sdk";
import { useDispatch } from "react-redux";

import { apiClient } from "@/api/apiClient";
import { getAzureSpeechTokenApiV1TranscriptionAzureSpeechTokenPost } from "@/api/generated/sdk.gen";
import { ConfirmationModal } from "@/components/ConfirmationModal";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranscription } from "@/pages/ReportStudio/ReportBuilder/store/useTranscription";
import { setMessage } from "@/redux/notification";
import { cn } from "@/utils/ui";

import { ReactComponent as LoadingIcon } from "@assets/svg/loading-spinner.svg";
import { ReactComponent as MicrophoneOnIcon } from "@assets/svg/mic-filled.svg";

import {
  isRecordingSessionLive,
  shouldYieldToActiveTranscriber,
  showRecordingConflictMessage,
} from "./active-session";
import { TranscriptionCallback } from "./hooks";
import { type LiveTranscriptTurn, appendLiveTurn } from "./segments";
import {
  AZURE_TOKEN_REFRESH_MS,
  LIVENESS_CHECK_INTERVAL_MS,
  MAX_RECONNECT_ATTEMPTS,
  RateLimitedStreamingError,
  SAMPLE_RATE,
  SILENCE_TOLERANCE_MS,
  type StreamingOptions,
  azureSpeechTokenBody,
  calculateReconnectBackoffMs,
  isStreamLikelyDead,
  isVoiceLevel,
  rateLimitMessage,
  retryAfterSeconds,
} from "./streaming";

export type TranscriberProps = {
  /** Callback object for handling transcription events */
  callback: TranscriptionCallback;
  /** Disable the transcriber button */
  disabled?: boolean;
  /** Optional className for the button */
  buttonClassName?: string;
  /** Icon size: sm, md, or lg */
  iconSize?: "sm" | "md" | "lg";
  /** Show text label alongside the icon */
  showLabel?: boolean;
  /** Custom idle-state label (defaults to "Transcribe"). Only rendered when showLabel is true. */
  label?: string;
  /**
   * Marks this as the app-wide persistent recording session (the one owned by
   * `RecordingHost`). Such a session is exempt from the single-active-transcriber
   * eviction rule: starting dictation elsewhere must never stop a live
   * assessment recording (MDRS-233).
   */
  isPersistentSession?: boolean;
};

export type TranscriberRef = {
  /**
   * Start a streaming session.
   */
  startTranscription: (
    options?: StreamingOptions
  ) => Promise<{ mediaStream: MediaStream } | null>;
  stopTranscription: () => void;
  /** Pause sending audio to Azure Speech without ending the session. */
  pauseTranscription: () => void;
  /** Resume sending audio after a pauseTranscription(). */
  resumeTranscription: () => void;
  isTranscribing: boolean;
  isPaused: boolean;
  /**
   * Swap in a fresh shared call-tab audio track mid-session (telehealth
   * "Re-share call tab" after the shared tab stopped sharing). No-op if the
   * session wasn't started with an `extraAudioTrack` mixer already in place —
   * a session that started in-person can't retroactively add call audio.
   */
  replaceCallAudioTrack: (track: MediaStreamTrack) => void;
  /**
   * The isolated microphone track (pre-mix), or null when not capturing. For
   * a telehealth session this is DIFFERENT from the mediaStream returned by
   * startTranscription (which is the mixed mic+call-tab stream) — used to
   * drive the "You" source card's own level meter (MDRS-268 ScribeSession).
   */
  getMicTrack: () => MediaStreamTrack | null;
  /** Get the full accumulated transcript from the current/last session */
  getFullTranscript: () => string;
  /**
   * Timestamped, speaker-less plain-transcript turns from the current/last
   * session — a UI-only viewing aid for the live feed, not persisted.
   */
  getTurns: () => LiveTranscriptTurn[];
  /** Current in-progress partial, or "" between turns. */
  getPartial: () => string;
};

// How long the "still there?" modal waits before treating silence as a
// real problem, rather than a normal pause in a long assessment. Kept in
// lockstep with the liveness watchdog's tolerance (see streaming.ts) so
// neither one nags/reconnects mid-silence while the other stays quiet.
const SILENCE_TIMEOUT_MS = SILENCE_TOLERANCE_MS;

// Perf marks for STP-88: measure time-to-first-text from button click so
// we can quantify "the transcription loads faster" before/after. Kept
// through the Azure migration — same metric, different engine.
const PERF_PREFIX = "medirect.transcriber.stp88";
const perfMark = (name: string): void => {
  try {
    performance.mark(`${PERF_PREFIX}.${name}`);
  } catch {
    /* performance.mark missing in some test envs */
  }
};
const perfMeasure = (name: string, start: string, end: string): number => {
  try {
    const m = performance.measure(
      `${PERF_PREFIX}.${name}`,
      `${PERF_PREFIX}.${start}`,
      `${PERF_PREFIX}.${end}`
    );
    return m.duration;
  } catch {
    return -1;
  }
};

export const Transcriber = forwardRef<TranscriberRef, TranscriberProps>(
  (
    {
      callback,
      disabled = false,
      buttonClassName,
      iconSize = "md",
      showLabel = false,
      label = "Transcribe",
      isPersistentSession = false,
    },
    ref
  ) => {
    const dispatch = useDispatch();
    const transcriberId = useId();
    const { setActiveTranscriber, isActive } = useTranscription();

    // Azure connection state. `recognizerRef` is the connection handle
    // (mirrors the old `wsRef`) — non-null iff a session is connected or
    // connecting. `pushStreamRef`/`speechConfigRef` are needed for
    // re-wiring audio after a mic re-acquire and for the token-refresh
    // timer respectively.
    const recognizerRef = useRef<ConversationTranscriber | null>(null);
    const pushStreamRef = useRef<PushAudioInputStream | null>(null);
    const speechConfigRef = useRef<SpeechConfig | null>(null);
    const speechRegionRef = useRef<string>("");
    const audioContextRef = useRef<AudioContext | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    // The isolated mic stream (pre-mix) — always mic-only, unlike
    // mediaStreamRef which is the telehealth-mixed stream once a call-tab
    // track is present. Exposed via getMicTrack() for the "You" level meter
    // (MDRS-268 ScribeSession source cards).
    const micStreamRef = useRef<MediaStream | null>(null);
    const workletNodeRef = useRef<AudioWorkletNode | null>(null);
    // The mixer destination node when a telehealth call-tab track is mixed in
    // (see acquireMicAndAudioGraph) — torn down alongside the audio graph.
    const mixDestNodeRef = useRef<MediaStreamAudioDestinationNode | null>(null);
    // The call-tab audio source node feeding the mixer, so replaceCallAudioTrack
    // can disconnect it and wire in a fresh track after a re-share.
    const callSourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
    const shouldContinueRef = useRef(false);
    const silenceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const reconnectAttemptsRef = useRef(0);
    // Set while a reconnect timer is pending so the liveness watchdog
    // doesn't stack a second reconnect on top of an already-scheduled one.
    const isReconnectingRef = useRef(false);
    // Refreshes the Azure authorization token before its ~10 min expiry —
    // see connectAzureTranscriber's onopen-equivalent (session started).
    const tokenRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Per-session streaming options. Held in a ref so connectAzureTranscriber
    // — used by both the initial connect AND every reconnect — rebuilds the
    // config with the same options after a drop.
    const streamingOptionsRef = useRef<StreamingOptions>({});

    // Plain-transcript turns, built from finalized recognition results.
    // sessionStartAtRef anchors timestamps; lastTurnEndSecRef tracks the
    // previous turn's end so each new turn starts where the last finished.
    const liveTurnsRef = useRef<LiveTranscriptTurn[]>([]);
    const sessionStartAtRef = useRef<number>(0);
    const lastTurnEndSecRef = useRef<number>(0);

    // Keep callback in a ref so event handlers always use the latest version
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    // Accumulate full transcript across all recognition results
    const fullTranscriptRef = useRef("");
    // Holds the latest partial transcript for the in-progress turn. Azure
    // emits multiple `transcribing` events per turn — only the `transcribed`
    // (final) event commits it. If the user stops mid-turn (or the
    // connection closes before the final event arrives), this is the only
    // place that text exists.
    const latestPartialRef = useRef("");

    // Liveness watchdog state: track the timestamps of (a) the last
    // speech-level frame written to the push stream (per isVoiceLevel —
    // NOT every frame, so ambient silence doesn't count) and (b) the last
    // recognition result received from Azure. If real speech is flowing
    // but results have stopped, the connection is silently dead.
    const lastFrameSentAtRef = useRef<number | null>(null);
    const lastResultAtRef = useRef<number>(0);
    const livenessTimerRef = useRef<NodeJS.Timeout | null>(null);
    // Bound handler so we can remove it on cleanup.
    const visibilityHandlerRef = useRef<(() => void) | null>(null);

    // Live mic-level indicator. The PCM worklet hands us a ~100ms chunk
    // of Int16 samples per message; we compute the peak amplitude there
    // and a rAF loop smooths it (peak-hold + decay) onto a CSS variable
    // so the button ring tracks the user's voice without strobing.
    const audioLevelRef = useRef(0);
    const displayLevelRef = useRef(0);
    const levelRafIdRef = useRef<number | null>(null);
    const buttonWrapperRef = useRef<HTMLSpanElement | null>(null);

    // Log time-to-first-text only once per session.
    const hasLoggedFirstTextRef = useRef(false);

    const [isTranscribing, setIsTranscribing] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    // Hot-path ref for gating PCM frame writes in the worklet→push-stream
    // handler. The React state is kept in sync for UI re-renders
    // (Pause/Resume button swap, indicator copy, timer) but the ref is what
    // the audio loop reads to avoid stale-closure issues.
    const isPausedRef = useRef(false);
    const [isPaused, setIsPaused] = useState(false);
    const [showSilenceModal, setShowSilenceModal] = useState(false);
    // Sticky modal shown when the reconnect budget is exhausted — replaces
    // the easy-to-miss transient toast that was the old failure surface.
    const [unrecoverableError, setUnrecoverableError] = useState(false);

    const getIconSizeClass = () => {
      switch (iconSize) {
        case "sm":
          return "size-3";
        case "lg":
          return "size-5";
        case "md":
        default:
          return "size-4";
      }
    };

    const clearSilenceTimer = useCallback(() => {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = null;
      }
    }, []);

    const startSilenceTimer = useCallback(() => {
      clearSilenceTimer();
      silenceTimerRef.current = setTimeout(() => {
        if (shouldContinueRef.current && recognizerRef.current) {
          setShowSilenceModal(true);
        }
      }, SILENCE_TIMEOUT_MS);
    }, [clearSilenceTimer]);

    const clearLivenessTimer = useCallback(() => {
      if (livenessTimerRef.current) {
        clearInterval(livenessTimerRef.current);
        livenessTimerRef.current = null;
      }
    }, []);

    const clearTokenRefreshTimer = useCallback(() => {
      if (tokenRefreshTimerRef.current) {
        clearInterval(tokenRefreshTimerRef.current);
        tokenRefreshTimerRef.current = null;
      }
    }, []);

    // Smooth the raw chunk-peak into a display value via rAF: keep the
    // peak, then decay slowly. The result is a ring that pops on speech
    // and fades on silence rather than strobing per audio frame. Throttles
    // DOM writes to one per animation frame (~60Hz max), well below the
    // 10Hz rate at which audio chunks arrive.
    const stopLevelRafLoop = useCallback(() => {
      if (levelRafIdRef.current !== null) {
        cancelAnimationFrame(levelRafIdRef.current);
        levelRafIdRef.current = null;
      }
      displayLevelRef.current = 0;
      buttonWrapperRef.current?.style.setProperty("--mic-level", "0");
    }, []);

    const startLevelRafLoop = useCallback(() => {
      if (levelRafIdRef.current !== null) return;
      const DECAY_PER_FRAME = 0.92;
      const tick = () => {
        const raw = audioLevelRef.current;
        // Peak-hold: instantly latch onto a louder sample, decay otherwise.
        const next =
          raw > displayLevelRef.current
            ? raw
            : displayLevelRef.current * DECAY_PER_FRAME;
        displayLevelRef.current = next;
        buttonWrapperRef.current?.style.setProperty("--mic-level", next.toFixed(3));
        levelRafIdRef.current = requestAnimationFrame(tick);
      };
      levelRafIdRef.current = requestAnimationFrame(tick);
    }, []);

    const cleanupTranscription = useCallback(() => {
      clearSilenceTimer();
      clearLivenessTimer();
      clearTokenRefreshTimer();
      stopLevelRafLoop();

      if (visibilityHandlerRef.current) {
        document.removeEventListener(
          "visibilitychange",
          visibilityHandlerRef.current
        );
        visibilityHandlerRef.current = null;
      }

      if (recognizerRef.current) {
        try {
          recognizerRef.current.stopTranscribingAsync(
            () => recognizerRef.current?.close(),
            (err) => {
              console.warn("Error stopping Azure transcriber:", err);
              recognizerRef.current?.close();
            }
          );
        } catch (err) {
          console.warn("Error closing Azure transcriber:", err);
        }
        recognizerRef.current = null;
      }

      if (pushStreamRef.current) {
        try {
          pushStreamRef.current.close();
        } catch {}
        pushStreamRef.current = null;
      }
      speechConfigRef.current = null;

      if (workletNodeRef.current) {
        try {
          workletNodeRef.current.disconnect();
        } catch {}
        workletNodeRef.current = null;
      }

      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch {}
        audioContextRef.current = null;
      }
      mixDestNodeRef.current = null;
      callSourceNodeRef.current = null;

      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
      // The isolated mic stream shares its tracks with mediaStreamRef when
      // no telehealth mix is active (same object) — already stopped above in
      // that case. When a mix IS active they're different streams wrapping
      // the same underlying mic track, so stop it here too; a track already
      // stopped is a harmless no-op.
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => track.stop());
        micStreamRef.current = null;
      }

      shouldContinueRef.current = false;
      reconnectAttemptsRef.current = 0;
      isReconnectingRef.current = false;
      lastFrameSentAtRef.current = null;
      lastResultAtRef.current = 0;
      hasLoggedFirstTextRef.current = false;
      isPausedRef.current = false;
      setIsPaused(false);
      setIsTranscribing(false);
      setIsProcessing(false);
    }, [
      clearSilenceTimer,
      clearLivenessTimer,
      clearTokenRefreshTimer,
      stopLevelRafLoop,
    ]);

    const stopTranscription = useCallback(() => {
      if (!recognizerRef.current && !audioContextRef.current) return;

      shouldContinueRef.current = false;
      setIsProcessing(true);

      // Flush any in-progress turn text — Azure may not emit a final
      // `transcribed` event before we close the connection.
      if (latestPartialRef.current) {
        fullTranscriptRef.current += latestPartialRef.current + " ";
        callbackRef.current.onTextInsert(latestPartialRef.current + " ", {});
        latestPartialRef.current = "";
      }
      // Clear the live preview chip so it doesn't linger after stop.
      if (callbackRef.current.onPartialUpdate) {
        callbackRef.current.onPartialUpdate("");
      }

      cleanupTranscription();

      if (isActive(transcriberId)) {
        setActiveTranscriber(null);
      }

      if (callbackRef.current.onEnd) {
        callbackRef.current.onEnd();
      }

      dispatch(
        setMessage({
          content: "Transcription stopped successfully",
          type: "info",
        })
      );
    }, [
      cleanupTranscription,
      dispatch,
      setActiveTranscriber,
      transcriberId,
      isActive,
    ]);

    // Pause / resume the live stream without ending the session. The Azure
    // connection stays open so its session context (and our reconnect
    // budget) is preserved.
    //
    // We do NOT suspend the AudioContext — the worklet keeps producing PCM and
    // the `isPausedRef` guard in the worklet handler drops those frames on the
    // floor while paused. Suspending/resuming the context was fragile when the
    // MediaStream is shared with other consumers (the in-panel waveform +
    // MediaRecorder): the worklet could fail to restart on resume, freezing
    // transcription while the waveform kept animating (MDRS-116). Keeping the
    // context running makes resume bulletproof — flipping the flag is all it
    // takes to start/stop sending.
    //
    // Separately, we also never call stopTranscribingAsync/close the push
    // stream during pause: a known Azure SDK issue causes
    // ConversationTranscriber to stop recognizing audio after repeated
    // Start/Stop cycles on the SAME PushAudioInputStream. Since pause only
    // ever stops feeding it audio (never actually stops/restarts the
    // transcriber or push stream), that failure mode doesn't apply here.
    const pauseTranscription = useCallback(() => {
      if (!recognizerRef.current || isPausedRef.current) return;
      isPausedRef.current = true;
      setIsPaused(true);

      // Silence watchdog measures "user fell quiet"; pause is an explicit
      // choice, not silence. Stop the timer so the "are you still there?" modal
      // doesn't pop while paused.
      clearSilenceTimer();
      // Freeze the mic-level ring at zero (no frames are being sent).
      stopLevelRafLoop();
      audioLevelRef.current = 0;
    }, [clearSilenceTimer, stopLevelRafLoop]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        if (recognizerRef.current || audioContextRef.current) {
          shouldContinueRef.current = false;
          cleanupTranscription();
        }
        if (isActive(transcriberId)) {
          setActiveTranscriber(null);
        }
      };
    }, [cleanupTranscription, transcriberId, isActive, setActiveTranscriber]);

    // Stop transcription if another transcriber becomes active — except for the
    // persistent recording session, which outranks dictation mics (MDRS-233).
    useEffect(() => {
      if (
        shouldYieldToActiveTranscriber({
          isTranscribing,
          isActiveTranscriber: isActive(transcriberId),
          isPersistentSession,
        })
      ) {
        stopTranscription();
      }
    }, [
      isTranscribing,
      isActive,
      transcriberId,
      stopTranscription,
      isPersistentSession,
    ]);

    // ---------------------------------------------------------------------
    // Audio graph (re-)acquisition
    //
    // Pulled out of startTranscription so the track.onended path can rebuild
    // just the mic + worklet without tearing down the rest of the session.
    // The browser ends a track when the OS swaps the default mic, the user
    // unplugs a USB headset, or after extended sleep — and there is no
    // explicit close event on the Azure connection, so the dictation would
    // otherwise silently produce no more text.
    // ---------------------------------------------------------------------
    const acquireMicAndAudioGraph = useCallback(async (): Promise<void> => {
      // Tear down anything still wired to the previous mic.
      if (workletNodeRef.current) {
        try {
          workletNodeRef.current.disconnect();
        } catch {}
        workletNodeRef.current = null;
      }
      mixDestNodeRef.current = null;
      callSourceNodeRef.current = null;
      if (audioContextRef.current) {
        try {
          await audioContextRef.current.close();
        } catch {}
        audioContextRef.current = null;
      }
      if (mediaStreamRef.current) {
        // For a telehealth (mixed) session this is the mixer destination
        // node's own output track — stopping it only tears down that output,
        // never the upstream mic or call-tab tracks (the call-tab track is
        // owned by useCallAudioShare and must outlive a mic re-acquire).
        mediaStreamRef.current.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((t) => t.stop());
        micStreamRef.current = null;
      }

      // getUserMedia and audioWorklet.addModule are mutually
      // independent — run them in parallel rather than serially. The
      // AudioContext can be constructed sync up-front; only the module
      // load actually awaits anything. Saves ~50-100ms on cold starts
      // (the worklet fetch piggybacks on the mic-permission roundtrip).
      const audioContext = new AudioContext({ sampleRate: SAMPLE_RATE });
      audioContextRef.current = audioContext;

      // `latency` is a non-standard hint that lib.dom.d.ts doesn't model
      // yet. Browsers that support it favour low-latency capture; those
      // that don't ignore the field. The cast is purely for the type
      // system — the runtime value is what the browser actually reads.
      // `deviceId` is only set from the Audio Setup step's mic picker —
      // omitted (as before) for every other session, which keeps today's
      // "whatever the OS considers default" behaviour.
      const micDeviceId = streamingOptionsRef.current.micDeviceId;
      const audioConstraints = {
        channelCount: 1,
        sampleRate: SAMPLE_RATE,
        echoCancellation: true,
        noiseSuppression: true,
        latency: 0.05,
        ...(micDeviceId ? { deviceId: { exact: micDeviceId } } : {}),
      } as MediaTrackConstraints;

      const [micResult, workletResult] = await Promise.allSettled([
        navigator.mediaDevices.getUserMedia({ audio: audioConstraints }),
        audioContext.audioWorklet.addModule("/worklets/pcm-processor.js"),
      ]);

      // If either branch failed, clean up the successful one (the outer
      // catch closes the AudioContext) and surface the most actionable
      // error. Mic errors take precedence because they're user-fixable
      // (permission denied, no device, etc.) — worklet errors are
      // internal and the user can't do anything about them directly.
      if (micResult.status === "rejected") {
        throw micResult.reason;
      }
      if (workletResult.status === "rejected") {
        micResult.value.getTracks().forEach((t) => t.stop());
        throw workletResult.reason;
      }

      const micStream = micResult.value;
      micStreamRef.current = micStream;

      // If the mic disappears while we're still recording, treat it as a
      // recoverable network-style blip — re-acquire (which re-wires the
      // worklet to the open push stream if any) and reconnect if needed.
      micStream.getTracks().forEach((track) => {
        track.onended = () => {
          if (!shouldContinueRef.current) return;
          console.warn(
            "[Transcriber] MediaStream track ended unexpectedly — attempting to re-acquire mic"
          );
          (async () => {
            try {
              await acquireMicAndAudioGraph();
              if (!recognizerRef.current) {
                attemptReconnect();
              }
            } catch (err) {
              console.error("[Transcriber] Failed to re-acquire mic:", err);
              attemptReconnect();
            }
          })();
        };
      });

      const workletNode = new AudioWorkletNode(audioContext, "pcm-processor");
      workletNodeRef.current = workletNode;

      // Telehealth: mix the shared call-tab audio in alongside the mic so
      // both the examiner and the remote examinee are transcribed and
      // recorded. Route both sources through a mixer destination node and
      // treat ITS stream as the session's media stream, so the worklet, the
      // MediaRecorder (see useRecordingSession), and everything else downstream
      // see one ordinary mixed stream — no telehealth-specific branching
      // needed anywhere but here. In-person sessions (no extraAudioTrack,
      // the default) skip the mixer entirely and behave exactly as before.
      const extraAudioTrack = streamingOptionsRef.current.extraAudioTrack;
      const micSource = audioContext.createMediaStreamSource(micStream);
      let sessionStream = micStream;
      if (extraAudioTrack && extraAudioTrack.readyState === "live") {
        // Mixer destination node: both sources connect INTO it (producing the
        // summed MediaStream used for recording), and separately both connect
        // straight into the worklet (for Azure) — the destination node itself
        // has no bearing on what the worklet hears, it only exists to produce
        // a MediaStream for the MediaRecorder/other consumers.
        const mixDest = audioContext.createMediaStreamDestination();
        mixDestNodeRef.current = mixDest;
        const callSource = audioContext.createMediaStreamSource(
          new MediaStream([extraAudioTrack])
        );
        callSourceNodeRef.current = callSource;
        micSource.connect(mixDest);
        callSource.connect(mixDest);
        callSource.connect(workletNode);
        sessionStream = mixDest.stream;
      }
      micSource.connect(workletNode);
      mediaStreamRef.current = sessionStream;

      // Connect to destination to keep the graph alive (audio is muted by worklet)
      workletNode.connect(audioContext.destination);

      // If a push stream is already open (mic re-acquire path), re-wire
      // frames to it.
      if (pushStreamRef.current) {
        wirePcmWorkletToPushStream(pushStreamRef.current);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Wire the AudioWorklet PCM output to the Azure push stream and track
    // speech-level frame writes for the liveness watchdog. We also peek at
    // the chunk's peak amplitude (Int16 view, normalised 0..1) to drive the
    // live mic-level ring around the button.
    const wirePcmWorkletToPushStream = useCallback(
      (pushStream: PushAudioInputStream) => {
        if (!workletNodeRef.current) return;
        workletNodeRef.current.port.onmessage = (event) => {
          // Paused — drop the frame on the floor. Azure sees no audio, the
          // liveness watchdog's "frames flowing" predicate stays false (so
          // it won't trip), and the connection stays open so resume reuses
          // the same session.
          if (isPausedRef.current) return;

          // Peak amplitude over the ~100ms chunk. Walking the 1600
          // samples is cheap (microseconds) and lets us avoid an
          // AnalyserNode + a second tap on the audio graph.
          const view = new Int16Array(event.data as ArrayBuffer);
          let peak = 0;
          for (let i = 0; i < view.length; i++) {
            const v = view[i] < 0 ? -view[i] : view[i];
            if (v > peak) peak = v;
          }
          audioLevelRef.current = peak / 32768;

          try {
            pushStream.write(event.data as ArrayBuffer);
          } catch (err) {
            // Stream already closed (e.g. a reconnect swapped it out from
            // under an in-flight worklet message) — safe to ignore, the
            // next connect cycle re-wires a fresh stream.
            console.warn("[Transcriber] Push stream write failed:", err);
            return;
          }

          // Only count this chunk toward the liveness watchdog if it
          // actually contains speech-level signal (see isVoiceLevel).
          // Ambient room noise during genuine silence would otherwise look
          // identical to "the doctor is dictating but Azure has gone
          // dead" to the watchdog below.
          if (isVoiceLevel(audioLevelRef.current)) {
            lastFrameSentAtRef.current = Date.now();
          }
        };
      },
      []
    );

    const resumeTranscription = useCallback(() => {
      if (!recognizerRef.current || !isPausedRef.current) return;

      // Reset liveness anchors so the watchdog doesn't fire on the long gap that
      // just elapsed during pause.
      lastFrameSentAtRef.current = null;
      lastResultAtRef.current = Date.now();
      startLevelRafLoop();
      startSilenceTimer();

      // The AudioContext was never suspended (see pauseTranscription), so the
      // worklet has been producing frames the whole time — the guard just
      // dropped them. Clearing the flag immediately starts sending again to the
      // push stream. If the graph is somehow gone (e.g. a co-consumer of the
      // shared MediaStream tore it down), rebuild it — acquireMicAndAudioGraph
      // re-wires to the still-open push stream automatically.
      const ctx = audioContextRef.current;
      if (!ctx || ctx.state === "closed" || !workletNodeRef.current) {
        acquireMicAndAudioGraph().catch((err) => {
          console.error(
            "[Transcriber] Failed to rebuild audio graph on resume:",
            err
          );
        });
      } else if (ctx.state === "suspended") {
        // Defensive: something else may have suspended it.
        ctx.resume().catch((err) => {
          console.warn("[Transcriber] AudioContext resume failed:", err);
        });
      }

      // Flip the guard LAST so the graph is ready before frames start flowing.
      isPausedRef.current = false;
      setIsPaused(false);
    }, [startLevelRafLoop, startSilenceTimer, acquireMicAndAudioGraph]);

    const startLivenessTimer = useCallback(() => {
      if (livenessTimerRef.current) {
        clearInterval(livenessTimerRef.current);
      }
      livenessTimerRef.current = setInterval(() => {
        if (!shouldContinueRef.current || !recognizerRef.current) return;
        if (isReconnectingRef.current) return;

        // AudioContext can be auto-suspended by Chrome/Safari for reasons
        // beyond tab visibility (autoplay heuristics, mobile background
        // suspend, OS audio focus loss when another app grabs the mic,
        // certain user-interaction patterns during the session). The
        // `visibilitychange` listener only catches tab focus; this
        // watchdog catches everything else. When suspended (but the
        // session isn't paused on purpose), the worklet stops producing
        // frames, no audio reaches Azure, and the user can speak into the
        // void until the silence modal pops. Auto-resume keeps the session
        // live without their intervention.
        const audioContext = audioContextRef.current;
        if (
          audioContext &&
          audioContext.state === "suspended" &&
          !isPausedRef.current
        ) {
          console.warn(
            "[Transcriber] Liveness watchdog: AudioContext suspended unexpectedly — resuming"
          );
          audioContext.resume().catch((err) => {
            console.warn("[Transcriber] AudioContext auto-resume failed:", err);
          });
          // Deliberately don't touch `lastFrameSentAtRef` here. The
          // worklet handler updates it naturally as PCM frames flow
          // post-resume; nudging it preemptively can mis-train the
          // dead-stream check below if frames don't actually resume
          // (e.g. permission revoked, mic device gone).
        }

        const dead = isStreamLikelyDead({
          lastTurnEventAt: lastResultAtRef.current,
          lastFrameSentAt: lastFrameSentAtRef.current,
          now: Date.now(),
        });
        if (dead) {
          console.warn(
            "[Transcriber] Liveness watchdog: frames flowing but no recognition results — forcing reconnect"
          );
          attemptReconnect();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, LIVENESS_CHECK_INTERVAL_MS);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch a fresh Azure Speech token. Shared by the initial connect,
    // reconnects, and the periodic refresh timer.
    const fetchAzureSpeechToken = useCallback(async (): Promise<{
      token: string;
      region: string;
    }> => {
      const tokenResponse =
        await getAzureSpeechTokenApiV1TranscriptionAzureSpeechTokenPost({
          client: apiClient,
          // The assessment recording session must never fall back to a dictation token.
          body: azureSpeechTokenBody(streamingOptionsRef.current.caseNumber, {
            requireCase: isPersistentSession,
          }),
        });
      const token = tokenResponse.data?.token;
      const region = tokenResponse.data?.region;
      if (tokenResponse.error || !token || !region) {
        if (tokenResponse.response?.status === 429) {
          throw new RateLimitedStreamingError(
            retryAfterSeconds(tokenResponse.response)
          );
        }
        throw new Error("Failed to get Azure Speech token");
      }
      return { token, region };
    }, [isPersistentSession]);

    // Start (or restart, on reconnect) the token-refresh timer. Azure
    // authorization tokens expire after ~10 minutes; assessments can run
    // far longer, so this keeps the session alive without a restart.
    // Critically, a refreshed token must be set on BOTH the SpeechConfig
    // AND the live ConversationTranscriber instance — the config's copy is
    // only read when a NEW recognizer is constructed, so updating just the
    // config would silently do nothing for an already-running session.
    const startTokenRefreshTimer = useCallback(() => {
      clearTokenRefreshTimer();
      tokenRefreshTimerRef.current = setInterval(async () => {
        if (!shouldContinueRef.current) return;
        const refresh = async () => {
          try {
            const { token } = await fetchAzureSpeechToken();
            if (speechConfigRef.current) {
              speechConfigRef.current.authorizationToken = token;
            }
            if (recognizerRef.current) {
              recognizerRef.current.authorizationToken = token;
            }
          } catch (err) {
            console.warn("[Transcriber] Azure token refresh failed:", err);
            if (err instanceof RateLimitedStreamingError) {
              // The current token expires before the next interval; retry once the limit allows.
              setTimeout(() => {
                if (shouldContinueRef.current) void refresh();
              }, err.retryAfterSec * 1000);
            }
            // Otherwise not fatal by itself — the liveness watchdog will catch it if
            // the stale token eventually causes the connection to die.
          }
        };
        await refresh();
      }, AZURE_TOKEN_REFRESH_MS);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clearTokenRefreshTimer, fetchAzureSpeechToken]);

    const connectAzureTranscriber = useCallback(
      (token: string, region: string): Promise<void> => {
        return new Promise((resolve, reject) => {
          const speechConfig = SpeechConfig.fromAuthorizationToken(token, region);
          speechConfig.speechRecognitionLanguage = "en-AU";

          // Azure's own service-side silence detection can end a session
          // before our client-side watchdogs (below) even get a say. Match
          // it to the same tolerance so a quiet room doesn't get the
          // session killed server-side ahead of schedule.
          speechConfig.setProperty(
            PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs,
            String(SILENCE_TOLERANCE_MS)
          );

          speechConfigRef.current = speechConfig;
          speechRegionRef.current = region;

          const pushStream = AudioInputStream.createPushStream(
            AudioStreamFormat.getWaveFormatPCM(SAMPLE_RATE, 16, 1)
          );
          pushStreamRef.current = pushStream;

          const audioConfig = AudioConfig.fromStreamInput(pushStream);
          const recognizer = new ConversationTranscriber(speechConfig, audioConfig);
          recognizerRef.current = recognizer;

          recognizer.sessionStarted = () => {
            perfMark("ws-open");
            reconnectAttemptsRef.current = 0;
            isReconnectingRef.current = false;
            // Seed liveness so the watchdog doesn't fire before the first
            // recognition result has had a chance to arrive.
            lastResultAtRef.current = Date.now();
            lastFrameSentAtRef.current = null;
            startLivenessTimer();
            startLevelRafLoop();
            startTokenRefreshTimer();

            setIsTranscribing(true);
            setIsProcessing(false);
            startSilenceTimer();

            dispatch(
              setMessage({
                content: "Transcription started successfully",
                type: "success",
              })
            );
          };

          const handleResult = (
            evt: ConversationTranscriptionEventArgs,
            isFinal: boolean
          ) => {
            const result = evt.result;

            // Final results only — Azure emits `transcribing` (partial)
            // events many times per turn, so logging every one would spam
            // the console for the whole duration of an hour-long session.
            // Structural fields only — transcript content is PHI (per
            // backend/architecture/security.md) and must not land in the
            // browser console.
            if (isFinal) {
              console.log("[Transcriber] recognition result:", {
                isFinal,
                length: result.text?.length ?? 0,
              });
            }

            // Any result (partial OR final) proves the connection is alive
            // and Azure is processing audio. Liveness updates on both so
            // the watchdog doesn't trip mid-utterance.
            lastResultAtRef.current = Date.now();

            const transcript = result.text?.trim() ?? "";

            // Log time-to-first-text once per session — the metric the
            // STP-88 perf change is meant to move.
            if (transcript && !hasLoggedFirstTextRef.current) {
              hasLoggedFirstTextRef.current = true;
              perfMark("first-text");
              const ttft = perfMeasure("time-to-first-text", "start", "first-text");
              if (ttft > 0) {
                console.info(
                  `[Transcriber] time-to-first-text: ${Math.round(ttft)}ms`
                );
              }
            }

            // Any non-empty transcript — partial or final — proves the
            // doctor is still speaking, so reset the silence watchdog and
            // dismiss the silence modal if it had already popped. Without
            // the auto-dismiss, a doctor who pauses for 11s, sees the
            // modal, then resumes would be dictating behind a dialog
            // that's stealing their cursor focus.
            if (transcript && shouldContinueRef.current) {
              startSilenceTimer();
              setShowSilenceModal(false);
            }

            if (isFinal) {
              if (transcript && !disabled && shouldContinueRef.current) {
                fullTranscriptRef.current += transcript + " ";
                callbackRef.current.onTextInsert(transcript + " ", {});

                // Fold this turn into the running plain-transcript turn list
                // and notify consumers so they can render a live view.
                //
                // Timestamps are derived from wall-clock elapsed since
                // session start (performance.now), NOT Azure's own word
                // timings — so they're approximate and will NOT match the
                // batch-diarized segmented_full_transcript exactly. These
                // turns are a UI-only viewing aid; don't assert equality
                // against the persisted copy.
                const nowSec =
                  (performance.now() - sessionStartAtRef.current) / 1000;
                liveTurnsRef.current = appendLiveTurn(liveTurnsRef.current, {
                  text: transcript,
                  startSec: lastTurnEndSecRef.current,
                  endSec: nowSec,
                });
                lastTurnEndSecRef.current = nowSec;
                callbackRef.current.onTurnsUpdate?.(liveTurnsRef.current);
              }
              latestPartialRef.current = "";
              if (callbackRef.current.onPartialUpdate) {
                callbackRef.current.onPartialUpdate("");
              }
            } else {
              latestPartialRef.current = transcript;
              if (callbackRef.current.onPartialUpdate && shouldContinueRef.current) {
                callbackRef.current.onPartialUpdate(transcript);
              }
            }
          };

          recognizer.transcribing = (_sender, evt) => {
            // Non-speech / low-confidence partials can arrive with empty
            // text or a NoMatch reason — handleResult's own empty-string
            // guards make this a harmless no-op either way.
            handleResult(evt, false);
          };

          recognizer.transcribed = (_sender, evt) => {
            if (evt.result.reason !== ResultReason.RecognizedSpeech) {
              // e.g. NoMatch — Azure detected the turn ended but couldn't
              // recognize speech in it. Nothing to commit.
              return;
            }
            handleResult(evt, true);
          };

          recognizer.canceled = (_sender, evt) => {
            console.warn("[Transcriber] Azure session canceled:", {
              reason: evt.reason,
              errorDetails: evt.errorDetails,
            });
            if (shouldContinueRef.current) {
              attemptReconnect();
            }
            reject(new Error("Azure Speech connection failed"));
          };

          recognizer.sessionStopped = () => {
            // Server-initiated graceful close — also a normal exit.
            cleanupTranscription();
            if (isActive(transcriberId)) {
              setActiveTranscriber(null);
            }
            if (callbackRef.current.onEnd) {
              callbackRef.current.onEnd();
            }
          };

          // Forward audio from worklet to the push stream (uses
          // lastFrameSentAt tracking for the liveness watchdog).
          wirePcmWorkletToPushStream(pushStream);

          recognizer.startTranscribingAsync(
            () => resolve(),
            (err) => reject(new Error(err))
          );
        });
      },
      [
        dispatch,
        disabled,
        startSilenceTimer,
        startLivenessTimer,
        startLevelRafLoop,
        startTokenRefreshTimer,
        cleanupTranscription,
        wirePcmWorkletToPushStream,
        isActive,
        transcriberId,
        setActiveTranscriber,
      ]
    );

    const attemptReconnect = useCallback(async () => {
      if (!shouldContinueRef.current) return;
      if (isReconnectingRef.current) return;

      if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
        clearLivenessTimer();
        clearSilenceTimer();
        // Sticky modal — the previous toast was easy to miss while the
        // doctor was looking at the patient. The session is dead; make it
        // obvious.
        setUnrecoverableError(true);
        cleanupTranscription();
        if (isActive(transcriberId)) {
          setActiveTranscriber(null);
        }
        if (callbackRef.current.onEnd) {
          callbackRef.current.onEnd();
        }
        return;
      }

      reconnectAttemptsRef.current += 1;
      isReconnectingRef.current = true;
      const delay = calculateReconnectBackoffMs(reconnectAttemptsRef.current);

      dispatch(
        setMessage({
          content: "Transcription interrupted. Attempting to reconnect...",
          type: "warning",
        })
      );

      setTimeout(async () => {
        if (!shouldContinueRef.current) return;

        try {
          // Close old recognizer + push stream
          if (recognizerRef.current) {
            try {
              recognizerRef.current.close();
            } catch {}
            recognizerRef.current = null;
          }
          if (pushStreamRef.current) {
            try {
              pushStreamRef.current.close();
            } catch {}
            pushStreamRef.current = null;
          }

          // If the mic also died (track.onended), re-acquire before opening
          // a new connection — otherwise the new session will sit silent.
          const tracks = mediaStreamRef.current?.getTracks() ?? [];
          const allTracksLive =
            tracks.length > 0 && tracks.every((t) => t.readyState === "live");
          if (!allTracksLive) {
            await acquireMicAndAudioGraph();
          }

          // Get a fresh token
          const { token, region } = await fetchAzureSpeechToken();

          // Must be awaited: connectAzureTranscriber's promise can reject
          // (recognizer.canceled, startTranscribingAsync's error callback)
          // after this attempt starts. Without awaiting, that rejection is
          // unhandled and never reaches the catch below — the only place
          // that resets isReconnectingRef.current — leaving every future
          // reconnect attempt permanently no-op'd on the guard at the top
          // of this function.
          await connectAzureTranscriber(token, region);
        } catch (err) {
          console.error("Reconnect attempt failed:", err);
          isReconnectingRef.current = false;
          if (err instanceof RateLimitedStreamingError) {
            // Waiting is the fix, so this attempt doesn't count against the budget.
            reconnectAttemptsRef.current = Math.max(
              0,
              reconnectAttemptsRef.current - 1
            );
            dispatch(
              setMessage({
                content: rateLimitMessage(err.retryAfterSec),
                type: "warning",
              })
            );
            setTimeout(() => {
              if (shouldContinueRef.current) attemptReconnect();
            }, err.retryAfterSec * 1000);
            return;
          }
          attemptReconnect();
        }
      }, delay);
    }, [
      dispatch,
      clearLivenessTimer,
      clearSilenceTimer,
      cleanupTranscription,
      connectAzureTranscriber,
      acquireMicAndAudioGraph,
      fetchAzureSpeechToken,
      isActive,
      transcriberId,
      setActiveTranscriber,
    ]);

    const startTranscription = async (
      options: StreamingOptions = {}
    ): Promise<{
      mediaStream: MediaStream;
    } | null> => {
      if (isTranscribing || recognizerRef.current || disabled) return null;

      // Capture per-session streaming options before the first connect so
      // both this connect and any later reconnects rebuild the config with
      // the same options.
      streamingOptionsRef.current = options;

      // Reset turn accumulation and anchor the session clock so turn
      // timestamps are relative to the moment recording starts.
      liveTurnsRef.current = [];
      lastTurnEndSecRef.current = 0;
      sessionStartAtRef.current = performance.now();

      // Reset accumulated transcript for new session
      fullTranscriptRef.current = "";
      latestPartialRef.current = "";
      isPausedRef.current = false;
      setIsPaused(false);
      setUnrecoverableError(false);

      setActiveTranscriber(transcriberId);

      if (callbackRef.current.onFocus) {
        callbackRef.current.onFocus();
      }

      setIsProcessing(true);
      shouldContinueRef.current = true;
      perfMark("start");
      hasLoggedFirstTextRef.current = false;

      try {
        // Mic+worklet setup and the Azure token fetch are independent —
        // kick them off concurrently rather than awaiting mic first then
        // token. Running them in parallel removes ~the smaller of the two
        // from the "click Start → see text" hot path.
        const startupResults = await Promise.allSettled([
          acquireMicAndAudioGraph(),
          fetchAzureSpeechToken(),
        ]);

        const micSetup = startupResults[0];
        const tokenSettled = startupResults[1];

        if (micSetup.status === "rejected") throw micSetup.reason;
        if (tokenSettled.status === "rejected") throw tokenSettled.reason;

        perfMark("mic-acquired");
        perfMark("token-fetched");

        const { token, region } = tokenSettled.value;

        // Resume the AudioContext when the tab is hidden then revealed
        // again — Chrome/Safari suspend background AudioContexts on some
        // platforms, which would silently drop all frames.
        const handleVisibilityChange = () => {
          if (
            document.visibilityState === "visible" &&
            audioContextRef.current?.state === "suspended"
          ) {
            audioContextRef.current.resume().catch((err) => {
              console.warn("[Transcriber] AudioContext resume failed:", err);
            });
          }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        visibilityHandlerRef.current = handleVisibilityChange;

        // Open the Azure connection — awaits until the session has started
        await connectAzureTranscriber(token, region);

        return { mediaStream: mediaStreamRef.current! };
      } catch (error) {
        console.error("Failed to start transcription:", error);

        // Full cleanup — mirrors the old WS-era guard against a partially
        // populated recognizerRef blocking retries. cleanupTranscription
        // nulls the recognizer, audio graph, mic, timers, and
        // isTranscribing/isProcessing in one go.
        cleanupTranscription();
        if (isActive(transcriberId)) {
          setActiveTranscriber(null);
        }
        // Notify the consumer that the session ended so it can undo any
        // optimistic state changes made on the onFocus that fired at the
        // top of startTranscription — without this, a caller that flipped
        // into "edit mode" on focus is stranded in edit mode forever.
        callbackRef.current.onEnd?.();

        let errorMessage = "Failed to start transcription. Please try again.";

        if (error instanceof DOMException && error.name === "NotAllowedError") {
          errorMessage =
            "Microphone access denied. Please allow microphone access to use transcription.";
        } else if (error instanceof DOMException && error.name === "NotFoundError") {
          errorMessage =
            "No microphone detected. Please connect a microphone and try again.";
        } else if (
          error instanceof DOMException &&
          error.name === "OverconstrainedError"
        ) {
          // Thrown by getUserMedia's exact-deviceId constraint when the mic
          // picked in the Audio Setup step has since been unplugged/
          // disconnected — "try again" alone doesn't tell the user WHAT to
          // fix, unlike the two cases above.
          errorMessage =
            "The selected microphone is no longer available. Please choose a different one and try again.";
        } else if (error instanceof RateLimitedStreamingError) {
          errorMessage = rateLimitMessage(error.retryAfterSec);
        }

        dispatch(
          setMessage({
            content: errorMessage,
            type: "error",
          })
        );

        return null;
      }
    };

    // Handle silence modal confirmation (stop transcription)
    const handleConfirmSilenceStop = useCallback(() => {
      setShowSilenceModal(false);
      stopTranscription();
    }, [stopTranscription]);

    // Handle silence modal cancellation (continue transcription)
    const handleCancelSilenceStop = useCallback(() => {
      setShowSilenceModal(false);
      // The silence modal is the user-visible symptom of a stalled audio
      // pipeline. If the cause was an auto-suspended AudioContext (most
      // common culprit when the user was actually speaking the whole
      // time) the watchdog above will have already resumed it, but
      // belt-and-braces: explicitly resume on Continue so an immediate
      // retry doesn't keep recording into the void.
      const audioContext = audioContextRef.current;
      if (audioContext && audioContext.state === "suspended") {
        audioContext.resume().catch((err) => {
          console.warn(
            "[Transcriber] AudioContext resume on silence-continue failed:",
            err
          );
        });
        // (See watchdog branch — leaving lastFrameSentAtRef to the worklet.)
      }
      startSilenceTimer();
    }, [startSilenceTimer]);

    // Swap in a fresh call-tab track after a telehealth re-share, without a
    // full mic re-acquire (which would also needlessly bounce the Azure
    // connection). No-op if this session has no mixer in place — either it
    // was started in-person, or the audio graph isn't live.
    const replaceCallAudioTrack = useCallback((track: MediaStreamTrack) => {
      const audioContext = audioContextRef.current;
      const mixDest = mixDestNodeRef.current;
      const workletNode = workletNodeRef.current;
      if (!audioContext || !mixDest || !workletNode) return;

      if (callSourceNodeRef.current) {
        try {
          callSourceNodeRef.current.disconnect();
        } catch {}
      }
      const callSource = audioContext.createMediaStreamSource(
        new MediaStream([track])
      );
      callSourceNodeRef.current = callSource;
      callSource.connect(mixDest);
      callSource.connect(workletNode);
      streamingOptionsRef.current = {
        ...streamingOptionsRef.current,
        extraAudioTrack: track,
      };
    }, []);

    /**
     * Starts dictation unless the persistent assessment recording holds the mic.
     *
     * The recording is exempt from active-transcriber eviction (MDRS-233), so
     * without this refusal both would capture at once: two getUserMedia streams
     * competing on one device, two billed Azure Speech streams, and the doctor's
     * speech landing in both the assessment transcript and the report field.
     * Refusing the newcomer is the safe direction — the in-flight recording is
     * the thing that must not be disturbed.
     *
     * EVERY user-initiated start must route through here, not `startTranscription`
     * directly, or it reintroduces the double-mic state.
     */
    const startTranscriptionIfPermitted = (options?: StreamingOptions) => {
      if (!isPersistentSession && isRecordingSessionLive()) {
        showRecordingConflictMessage(dispatch);
        return;
      }
      startTranscription(options);
    };

    // Toggle between start and stop
    const handleToggleTranscription = () =>
      isTranscribing ? stopTranscription() : startTranscriptionIfPermitted();

    // Expose methods to parent component via ref
    useImperativeHandle(
      ref,
      () => ({
        startTranscription,
        stopTranscription,
        pauseTranscription,
        resumeTranscription,
        isTranscribing,
        isPaused,
        replaceCallAudioTrack,
        getMicTrack: () => micStreamRef.current?.getAudioTracks()[0] ?? null,
        getFullTranscript: () => fullTranscriptRef.current.trim(),
        getTurns: () => [...liveTurnsRef.current],
        getPartial: () => latestPartialRef.current,
      }),
      [
        stopTranscription,
        pauseTranscription,
        resumeTranscription,
        isTranscribing,
        isPaused,
        replaceCallAudioTrack,
      ]
    );

    return (
      <>
        <ConfirmationModal
          open={showSilenceModal}
          onOpenChange={(open) => {
            if (!open) {
              handleCancelSilenceStop();
            }
          }}
          type="warning"
          icon={<MicOff className="size-12 text-destructive" />}
          title="Stop Transcription?"
          description="No speech has been detected recently. Would you like to stop transcribing or continue?"
          confirmButtonText="Stop Transcription"
          cancelButtonText="Continue"
          onConfirm={handleConfirmSilenceStop}
          onCancel={handleCancelSilenceStop}
        />
        <ConfirmationModal
          open={unrecoverableError}
          onOpenChange={(open) => {
            if (!open) setUnrecoverableError(false);
          }}
          type="warning"
          icon={<MicOff className="size-12 text-destructive" />}
          title="Dictation stopped"
          description={
            "We lost the connection to the transcription service and couldn't reconnect after multiple attempts. " +
            "Your transcript so far has been preserved. Tap Restart dictation to continue."
          }
          confirmButtonText="Restart dictation"
          cancelButtonText="Close"
          onConfirm={() => {
            setUnrecoverableError(false);
            // Guarded: a dictation mic that exhausted its reconnect budget must
            // still not restart on top of a live assessment recording. Keeps the
            // session's case (consent gate), mic and call audio (MDRS-560).
            startTranscriptionIfPermitted(streamingOptionsRef.current);
          }}
          onCancel={() => setUnrecoverableError(false)}
        />
        <div className="flex items-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <span ref={buttonWrapperRef} className="relative inline-flex">
                {isTranscribing && (
                  // Live mic-level ring. Sits behind the button via
                  // pointer-events-none + a negative inset so it can't
                  // intercept clicks. `--mic-level` is updated per rAF
                  // frame (0..1) by startLevelRafLoop. Scale grows the
                  // halo with voice; opacity makes it visible only when
                  // sound is present, so a muted mic shows no ring and
                  // the user knows immediately something is wrong.
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute -inset-1 rounded-full bg-red-500"
                    style={
                      {
                        opacity: "calc(0.15 + var(--mic-level, 0) * 0.55)",
                        transform: "scale(calc(1 + var(--mic-level, 0) * 0.35))",
                        transition: "transform 60ms linear, opacity 60ms linear",
                      } as React.CSSProperties
                    }
                  />
                )}
                <Button
                  // Icon-only mode stays ghost (unobtrusive, inline with text).
                  // The labelled mode (header/toolbar buttons like "Start
                  // dictation") reads as a real button, so it gets the same
                  // outlined treatment as its neighbours — blue secondary-subtle
                  // while idle, destructive-outline while recording (mirrors the
                  // Edit button's simple-outline next to it).
                  variant={
                    showLabel
                      ? isTranscribing
                        ? "destructive-outline"
                        : "secondary-subtle"
                      : "ghost"
                  }
                  size={showLabel ? "sm" : "icon"}
                  className={cn(
                    "relative transition-colors",
                    !showLabel &&
                      cn(
                        "size-8 rounded-md",
                        isTranscribing
                          ? "bg-red-50 text-red-600 hover:bg-red-100"
                          : "hover:bg-gray-100 hover:text-gray-800"
                      ),
                    buttonClassName
                  )}
                  aria-label={
                    isTranscribing ? "stop transcribing" : "start transcription"
                  }
                  onClick={handleToggleTranscription}
                  disabled={isProcessing || disabled}
                >
                  {isProcessing ? (
                    <>
                      <LoadingIcon
                        className={cn(
                          showLabel ? "mr-1 size-4" : getIconSizeClass(),
                          "animate-spin-ccw text-primary-600"
                        )}
                      />
                      {showLabel && <span>Starting...</span>}
                    </>
                  ) : isTranscribing ? (
                    <>
                      {/* MDRS-266: MicrophoneOnIcon (no slash), not the
                          mic-off glyph — a red strikethrough mic reads as
                          "muted" to users, who then click to "unmute" and
                          instead call stopTranscription(), killing the
                          session mid-recording. Red fill still signals "hot". */}
                      <MicrophoneOnIcon
                        className={cn(
                          showLabel ? "mr-1 size-4" : getIconSizeClass(),
                          // mic-filled.svg hardcodes fill on its <path>, so a
                          // bare `fill-destructive` on the root is a no-op —
                          // override the path directly (same pattern as
                          // TranscribeAndUploadButtons.tsx's use of this icon).
                          "[&_path]:fill-destructive"
                        )}
                      />
                      {showLabel && <span>Stop</span>}
                    </>
                  ) : (
                    <>
                      <MicrophoneOnIcon
                        className={showLabel ? "mr-1 size-4" : getIconSizeClass()}
                      />
                      {showLabel && <span>{label}</span>}
                    </>
                  )}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              {isProcessing
                ? "Starting dictation"
                : isTranscribing
                  ? "Stop dictation"
                  : "Start dictation"}
            </TooltipContent>
          </Tooltip>
        </div>
      </>
    );
  }
);

Transcriber.displayName = "Transcriber";
